import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  PoundSterling,
  Plus,
  RefreshCw,
  CheckCircle2,
  FileText
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const IncomeSection = () => {
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: "", startDate: "", endDate: "" });
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: "",
    category: "tithe",
    notes: "",
    reference: ""
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [entriesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/income`, { params: filter }),
        axios.get(`${API}/income/summary`, { params: filter })
      ]);
      setIncomeEntries(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error loading income data:", error);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleAddEntry = async () => {
    if (!newEntry.amount || !newEntry.date) return;
    try {
      await axios.post(`${API}/income`, {
        ...newEntry,
        amount: parseFloat(newEntry.amount)
      });
      setNewEntry({ date: new Date().toISOString().split('T')[0], amount: "", category: "tithe", notes: "", reference: "" });
      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error("Error adding income entry:", error);
    }
  };

  const categories = [
    { value: "tithe", label: "Tithe", color: "emerald" },
    { value: "offering", label: "Offering", color: "blue" },
    { value: "donation", label: "Donation", color: "purple" },
    { value: "program", label: "Program", color: "amber" },
    { value: "other", label: "Other", color: "slate" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500">
              <PoundSterling className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Church Income</h2>
              <p className="text-sm text-slate-500">Track tithes, offerings, and donations</p>
            </div>
          </div>
          <div className="flex gap-2">
            <motion.button
              onClick={loadData}
              className="px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </motion.button>
            <motion.button
              onClick={() => setShowAddForm(!showAddForm)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-medium shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4" />
              Add Entry
            </motion.button>
          </div>
        </div>

        {/* Add Entry Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-6 p-4 rounded-2xl bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200"
          >
            <h3 className="font-semibold text-emerald-800 mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5" />
              New Income Entry
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              <input
                type="date"
                className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 transition-all outline-none"
                value={newEntry.date}
                onChange={e => setNewEntry(prev => ({ ...prev, date: e.target.value }))}
              />
              <input
                type="number"
                step="0.01"
                className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 transition-all outline-none"
                placeholder="Amount"
                value={newEntry.amount}
                onChange={e => setNewEntry(prev => ({ ...prev, amount: e.target.value }))}
              />
              <select
                className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 transition-all outline-none"
                value={newEntry.category}
                onChange={e => setNewEntry(prev => ({ ...prev, category: e.target.value }))}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              <input
                className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 transition-all outline-none"
                placeholder="Reference (optional)"
                value={newEntry.reference}
                onChange={e => setNewEntry(prev => ({ ...prev, reference: e.target.value }))}
              />
              <input
                className="border-2 border-emerald-200 rounded-xl px-4 py-3 focus:border-emerald-400 transition-all outline-none md:col-span-2"
                placeholder="Notes (optional)"
                value={newEntry.notes}
                onChange={e => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <motion.button
              onClick={handleAddEntry}
              className="mt-4 w-full px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <CheckCircle2 className="w-5 h-5" />
              Save Entry
            </motion.button>
          </motion.div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border border-emerald-200">
            <p className="text-sm text-emerald-700">Grand Total</p>
            <p className="text-2xl font-bold text-emerald-900">${summary.grand_total?.toLocaleString() || 0}</p>
          </div>
          {categories.slice(0, 4).map(cat => (
            <div key={cat.value} className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <p className="text-sm text-slate-600">{cat.label}</p>
              <p className="text-xl font-bold text-slate-800">${(summary.by_category?.[cat.value] || 0).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Entries List */}
      <div className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100">
        <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-slate-500" />
          Recent Entries ({incomeEntries.length})
        </h3>
        
        {loading ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : incomeEntries.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <PoundSterling className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-slate-600 font-medium">No income entries yet</p>
            <p className="text-sm text-slate-500 mt-1">Click "Add Entry" to record income</p>
          </div>
        ) : (
          <div className="space-y-3">
            {incomeEntries.slice(0, 20).map((entry, index) => {
              const cat = categories.find(c => c.value === entry.category) || categories[4];
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-xl bg-emerald-500">
                        <PoundSterling className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">${entry.amount?.toLocaleString()}</p>
                        <p className="text-sm text-slate-600">{cat.label} • {entry.date}</p>
                        {entry.notes && <p className="text-xs text-slate-500 mt-1">{entry.notes}</p>}
                      </div>
                    </div>
                    {entry.reference && (
                      <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-xs font-medium">
                        Ref: {entry.reference}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncomeSection;
